import * as React from "react";
import { AuthLayout } from "../layout";
import { inject, observer } from "mobx-react";
import { MobxComponent } from "../../../../mobx/components/index";
import { Link } from "../../../components/router/Link";
import { UI } from "../../../../core/ui";
import { validators } from "@lib/common";
import { logger } from "@lib/common";
import { PasswordInput } from "../components/password-input";
import {
	AuthHeading,
	AuthSubheading,
	FooterLinks,
	FormField,
	FormLabel,
	InfoText,
	SubmitButton,
} from "./login.form.c";

interface Props { }
interface State {
	loading: boolean;
	password: string;
	confirm_password: string;
}

@inject("store") @observer
export class AuthPasswordResetChange extends MobxComponent<Props, State> {

	constructor(props: Props) {
		super(props);
		this.state = {
			loading: false,
			password: "",
			confirm_password: "",
		};
	}

	load = (loading: boolean) => {
		this.setState({ loading });
	}

	onSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
		e.preventDefault();
		const { store } = this.injected;
		try {

			const { password, confirm_password } = this.state;

			const { token, _id } = store.router.s.query;

			if (validators.password(password).error) {
				return UI.notification.error("Minimum password length is 5 characters");
			}
			else if (password !== confirm_password) {
				return UI.notification.error("Passwords do not match");
			}

			this.load(true);

			const data = await store.api.user_password_reset_change({ _id, token, password });

			this.load(false);

			if (data.outcome) {
				UI.notification.error(data.message, { timeout: 6000 });
			}
			else {
				UI.notification.success("Password changed successfully", { timeout: 6000 });
				store.router.push("/login");
			}

		}
		catch (e) {
			logger.captureException(e);
			UI.notification.error("Something went wrong, try again or contact us", { timeout: 6000 });
			this.load(false);
		}
		return null;
	}

	onChange = <T extends keyof State>(e: React.ChangeEvent<HTMLInputElement>) => {
		const newState = UI.helpers.handleChange(e);
		this.setState(newState as { [P in T]: State[P]; });
	}

	render() {
		const { loading, password, confirm_password } = this.state;
		const { query } = this.injected.store.router.s;
		const valid = !!query.token && !!query._id;
		return (
			<AuthLayout loading={loading}>
				<AuthHeading>Reset password</AuthHeading>
				{!valid && (
					<InfoText>
						Invalid password reset link. Please follow the link in the email, request another reset link or contact us
					</InfoText>
				)}
				{valid && (
					<>
						<AuthSubheading>Choose a new password for your account.</AuthSubheading>
						<form onSubmit={this.onSubmit}>
							<FormField>
								<FormLabel htmlFor="new-password">New password</FormLabel>
								<PasswordInput
									id="new-password"
									name="password"
									placeholder="••••••••"
									value={password}
									onChange={this.onChange}
									autoComplete="new-password"
									required={true}
									disabled={loading}
								/>
							</FormField>

							<FormField>
								<FormLabel htmlFor="confirm-new-password">Confirm password</FormLabel>
								<PasswordInput
									id="confirm-new-password"
									name="confirm_password"
									placeholder="••••••••"
									value={confirm_password}
									onChange={this.onChange}
									autoComplete="new-password"
									required={true}
									disabled={loading}
								/>
							</FormField>

							<SubmitButton type="submit" disabled={loading}>
								{loading ? (
									<>
										<svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
											<circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" opacity="0.25" />
											<path fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" opacity="0.75" />
										</svg>
										Resetting…
									</>
								) : (
									"Reset Password"
								)}
							</SubmitButton>

							<FooterLinks>
								<p>Remember your password? <Link to="/login">Login</Link></p>
							</FooterLinks>
						</form>
					</>
				)}
			</AuthLayout>
		);
	}

}
